import { useState } from 'react';
import axios from 'axios';
import 'bootstrap-icons/font/bootstrap-icons.css';

/**
 * TwoFactorSetup
 * props:
 *   - twoFactorEnabled (boolean)  — huidige 2FA status
 *   - onEnabled (function)        — callback zodra 2FA succesvol is ingeschakeld
 *   - variant (string)            — 'banner' (home) | 'card' (profiel), default 'card'
 */
const TwoFactorSetup = ({ twoFactorEnabled, onEnabled, variant = 'card' }) => {
  const token = sessionStorage.getItem('jwtToken');

  const [step, setStep] = useState(null); // null | 'qr' | 'done'
  const [qrCode, setQrCode] = useState('');
  const [setupKey, setSetupKey] = useState('');
  const [showSetupKey, setShowSetupKey] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [error, setError] = useState('');

  const handleSetup = async () => {
    setError('');
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/2fa/setup`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setQrCode(res.data.qrCode);
      setSetupKey(res.data.secret);
      setStep('qr');
    } catch (err) {
      setError('Kon 2FA setup niet starten. Probeer opnieuw.');
    }
  };

  const handleVerify = async () => {
    setError('');
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/2fa/verify`,
        { token: totpCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStep('done');
      setTotpCode('');
      if (onEnabled) onEnabled();
    } catch (err) {
      setError(err.response?.data?.error || 'Ongeldige code. Probeer opnieuw.');
    }
  };

  const handleCancel = () => {
    setStep(null);
    setQrCode('');
    setSetupKey('');
    setShowSetupKey(false);
    setTotpCode('');
    setError('');
  };

  // --- Succesmelding (gedeeld) ---
  if (step === 'done') {
    return (
      <div className="alert alert-success d-flex align-items-center gap-2 py-2 mb-4" style={{ fontSize: '13px' }}>
        <i className="bi bi-check-circle-fill"></i>
        2FA is succesvol ingeschakeld. Vanaf je volgende login wordt er een code gevraagd.
      </div>
    );
  }

  // --- QR flow (gedeeld) ---
  if (step === 'qr') {
    return (
      <div className="card mb-4 border-0 shadow-sm">
        <div className="card-body">
          <p className="mb-1 fw-semibold" style={{ fontSize: '14px' }}>
            Stap 1 — Koppel je authenticator app
          </p>
          <p className="text-muted mb-3" style={{ fontSize: '13px' }}>
            Open je authenticator app (Google Authenticator, Authy, etc.) en scan de QR-code, of voer de sleutel handmatig in.
          </p>

          <div className="mb-2">
            <img src={qrCode} alt="2FA QR code" style={{ width: 160, height: 160 }} />
          </div>

          <div className="mb-3">
            <button
              type="button"
              className="btn btn-link p-0 text-decoration-none"
              style={{ fontSize: '13px' }}
              onClick={() => setShowSetupKey((prev) => !prev)}
            >
              <i className={`bi ${showSetupKey ? 'bi-chevron-up' : 'bi-chevron-down'} me-1`}></i>
              {showSetupKey ? 'Verberg handmatige sleutel' : 'Kan de QR-code niet scannen? Gebruik handmatige sleutel'}
            </button>
            {showSetupKey && (
              <div className="mt-2 p-3 rounded" style={{ backgroundColor: '#f8f9fa', maxWidth: 360 }}>
                <p className="text-muted mb-1" style={{ fontSize: '12px' }}>
                  Kies in je app voor "Voer sleutel in" en vul het volgende in:
                </p>
                <div className="d-flex align-items-center gap-2">
                  <code
                    className="flex-grow-1 p-2 rounded"
                    style={{ backgroundColor: '#e9ecef', fontSize: '13px', letterSpacing: '0.05em', wordBreak: 'break-all' }}
                  >
                    {setupKey}
                  </code>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => navigator.clipboard.writeText(setupKey)}
                    title="Kopieer sleutel"
                  >
                    <i className="bi bi-clipboard"></i>
                  </button>
                </div>
              </div>
            )}
          </div>

          <p className="mb-1 fw-semibold" style={{ fontSize: '14px' }}>
            Stap 2 — Voer de code in
          </p>
          <p className="text-muted mb-2" style={{ fontSize: '13px' }}>
            Voer de 6-cijferige code uit je app in om de koppeling te bevestigen.
          </p>
          <div className="d-flex gap-2 align-items-center" style={{ maxWidth: 280 }}>
            <input
              type="text"
              className="form-control text-center"
              placeholder="000000"
              maxLength={6}
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
              autoFocus
            />
            <button
              className="btn btn-success btn-sm px-3"
              onClick={handleVerify}
              disabled={totpCode.length !== 6}
            >
              Bevestig
            </button>
            <button
              className="btn btn-outline-secondary btn-sm px-3"
              onClick={handleCancel}
            >
              Annuleer
            </button>
          </div>
          {error && (
            <div className="alert alert-danger mt-2 py-2" style={{ fontSize: '13px' }}>
              {error}
            </div>
          )}
        </div>
      </div>
    );
  }

  // --- Nog niet actief: banner (home) ---
  if (variant === 'banner' && !twoFactorEnabled) {
    return (
      <div
        className="d-flex align-items-center justify-content-between flex-wrap gap-2 rounded p-3 mb-4"
        style={{ backgroundColor: '#fffbeb', border: '1px solid #fcd34d' }}
      >
        <div className="d-flex align-items-center gap-3">
          <i className="bi bi-shield-exclamation" style={{ fontSize: '1.4rem', color: '#d97706' }}></i>
          <div>
            <div className="fw-semibold" style={{ fontSize: '15px', color: '#92400e' }}>
              Beveilig je account met twee-factor authenticatie
            </div>
            <div style={{ fontSize: '13px', color: '#b45309' }}>
              Schakel 2FA in voor extra beveiliging bij het inloggen.
            </div>
          </div>
        </div>
        <button
          className="btn btn-warning btn-sm rounded-pill px-3"
          style={{ fontSize: '13px', whiteSpace: 'nowrap' }}
          onClick={handleSetup}
        >
          <i className="bi bi-shield-plus me-2"></i>2FA inschakelen
        </button>
        {error && (
          <div className="alert alert-danger w-100 py-2 mb-0 mt-1" style={{ fontSize: '13px' }}>
            {error}
          </div>
        )}
      </div>
    );
  }

  // --- Card variant (profiel) ---
  return (
    <div className="card mb-4 border-0 shadow-sm">
      <div className="card-body">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div className="d-flex align-items-center gap-3">
            <div
              className="rounded-circle d-flex align-items-center justify-content-center"
              style={{
                width: 42,
                height: 42,
                backgroundColor: twoFactorEnabled ? '#d1fae5' : '#f3f4f6',
                flexShrink: 0,
              }}
            >
              <i
                className={`bi ${twoFactorEnabled ? 'bi-shield-fill-check' : 'bi-shield'}`}
                style={{ fontSize: '1.2rem', color: twoFactorEnabled ? '#059669' : '#6b7280' }}
              ></i>
            </div>
            <div>
              <div className="fw-semibold" style={{ fontSize: '15px' }}>
                Twee-factor authenticatie
                {twoFactorEnabled && (
                  <span
                    className="ms-2 badge"
                    style={{ backgroundColor: '#d1fae5', color: '#065f46', fontSize: '11px' }}
                  >
                    Actief
                  </span>
                )}
              </div>
              <div className="text-muted" style={{ fontSize: '13px' }}>
                {twoFactorEnabled
                  ? 'Je account is beveiligd met een authenticator app.'
                  : 'Voeg een extra beveiligingslaag toe aan je account.'}
              </div>
            </div>
          </div>

          {!twoFactorEnabled && (
            <button
              className="btn btn-primary btn-sm rounded-pill px-3"
              onClick={handleSetup}
            >
              <i className="bi bi-shield-plus me-2"></i>2FA inschakelen
            </button>
          )}
        </div>
        {error && (
          <div className="alert alert-danger mt-3 py-2 mb-0" style={{ fontSize: '13px' }}>
            {error}
          </div>
        )}
      </div>
    </div>
  );
};

export default TwoFactorSetup;